import { useEffect, useRef, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Copy,
  Save,
  Sparkles,
  Image as ImageIcon,
  Download,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListFeelings,
  adminCreateFeeling,
  adminUpdateFeeling,
  adminDeleteFeeling,
  adminReorderFeelings,
  adminListExperiences,
  adminCreateExperience,
  adminDeleteExperience,
  adminDuplicateExperience,
  adminReorderExperiences,
  adminGetGlimpseSettings,
  adminUpdateGlimpseSettings,
  uploadGlimpseImage,
  resolveMedia,
  exportGlimpseCsv,
  importGlimpseCsv,
} from "@/api/client";
import { StoryEditor } from "./StoryEditor";

const inputCls =
  "w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50";

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return null;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

export const GlimpseWorkspace = () => {
  const [tab, setTab] = useState("stories");
  const [feelings, setFeelings] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expsByFeeling, setExpsByFeeling] = useState({});
  const [selectedExpId, setSelectedExpId] = useState(null);
  const [newFeeling, setNewFeeling] = useState({ icon: "", label: "" });

  const refreshFeelings = () => adminListFeelings().then(setFeelings);
  useEffect(() => {
    refreshFeelings().catch(() => toast.error("Could not load feelings"));
  }, []);

  const loadExps = async (fid) => {
    const list = await adminListExperiences(fid);
    setExpsByFeeling((p) => ({ ...p, [fid]: list }));
    return list;
  };

  const toggleFeeling = async (fid) => {
    if (expandedId === fid) {
      setExpandedId(null);
      return;
    }
    setExpandedId(fid);
    if (!expsByFeeling[fid]) await loadExps(fid).catch(() => {});
  };

  const addFeeling = async () => {
    if (!newFeeling.label.trim()) return toast.error("Enter a feeling label");
    try {
      await adminCreateFeeling(newFeeling);
      setNewFeeling({ icon: "", label: "" });
      await refreshFeelings();
      toast.success("Feeling added");
    } catch {
      toast.error("Could not add feeling");
    }
  };

  const saveFeeling = async (fid, patch) => {
    try {
      await adminUpdateFeeling(fid, patch);
      await refreshFeelings();
    } catch {
      toast.error("Could not update feeling");
    }
  };

  const removeFeeling = async (f) => {
    if (
      !window.confirm(
        `Delete "${f.label}" and its ${f.experience_count} story/stories? This cannot be undone.`
      )
    )
      return;
    await adminDeleteFeeling(f.feeling_id);
    if (expandedId === f.feeling_id) setExpandedId(null);
    await refreshFeelings();
    toast.success("Feeling deleted");
  };

  const reorderFeeling = async (i, dir) => {
    const next = move(feelings, i, dir);
    if (!next) return;
    setFeelings(next);
    await adminReorderFeelings(next.map((f) => f.feeling_id)).catch(() => {});
  };

  // ---- stories ----
  const addStory = async (fid) => {
    const created = await adminCreateExperience({
      feeling_id: fid,
      option_title: "Untitled story",
      cards: [
        { type: "FEEL", title: "", body: "", show_image: false, image: "", alt: "" },
        { type: "BHĀGAVATAM", title: "THIS WAS ONLY A GLIMPSE.", body: "", show_image: false, image: "", alt: "" },
      ],
    });
    await loadExps(fid);
    await refreshFeelings();
    setSelectedExpId(created.experience_id);
    toast.success("Story created (draft)");
  };
  const duplicateStory = async (fid, eid) => {
    await adminDuplicateExperience(eid);
    await loadExps(fid);
    await refreshFeelings();
    toast.success("Story duplicated");
  };
  const removeStory = async (fid, eid) => {
    if (!window.confirm("Delete this story?")) return;
    await adminDeleteExperience(eid);
    if (selectedExpId === eid) setSelectedExpId(null);
    await loadExps(fid);
    await refreshFeelings();
    toast.success("Story deleted");
  };
  const reorderStory = async (fid, i, dir) => {
    const list = expsByFeeling[fid] || [];
    const next = move(list, i, dir);
    if (!next) return;
    setExpsByFeeling((p) => ({ ...p, [fid]: next }));
    await adminReorderExperiences(next.map((e) => e.experience_id)).catch(() => {});
  };

  if (!feelings) {
    return (
      <div className="grid place-items-center py-20 text-amber-100/40">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="glimpse-workspace">
      <div className="mb-6 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-amber-300" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
          Bhāgavatam Experience
        </h2>
        <div className="ml-auto flex gap-2">
          {[
            { key: "stories", label: "Stories" },
            { key: "settings", label: "Experience Settings" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`glimpse-ws-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                tab === t.key
                  ? "bg-amber-400 text-[#0a0f24]"
                  : "border border-amber-300/25 text-amber-200 hover:bg-amber-400/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "settings" && <ExperienceSettings />}

      {tab === "stories" && (
        <>
          <CsvToolbar onImported={() => { refreshFeelings(); setExpsByFeeling({}); setExpandedId(null); setSelectedExpId(null); }} />
          <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr]">
          {/* --- feelings + stories tree --- */}
          <div className="space-y-3">
            <div className="rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid="new-feeling-icon"
                  value={newFeeling.icon}
                  onChange={(e) => setNewFeeling((p) => ({ ...p, icon: e.target.value }))}
                  placeholder="😊"
                  className="w-14 rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-2 py-2 text-center text-sm outline-none focus:border-amber-300/50"
                />
                <input
                  data-testid="new-feeling-label"
                  value={newFeeling.label}
                  onChange={(e) => setNewFeeling((p) => ({ ...p, label: e.target.value }))}
                  placeholder="New feeling label"
                  className={inputCls}
                  onKeyDown={(e) => e.key === "Enter" && addFeeling()}
                />
                <button
                  data-testid="add-feeling-btn"
                  onClick={addFeeling}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-400 px-3 py-2 text-xs font-semibold text-[#0a0f24] hover:bg-amber-300"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              </div>
            </div>

            {feelings.map((f, i) => (
              <div
                key={f.feeling_id}
                data-testid={`feeling-row-${f.feeling_id}`}
                className="overflow-hidden rounded-2xl border border-amber-300/12 bg-[#0d1226]/70"
              >
                <div className="flex items-center gap-2 p-3">
                  <button
                    onClick={() => toggleFeeling(f.feeling_id)}
                    className="text-amber-100/50 hover:text-amber-200"
                  >
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        expandedId === f.feeling_id ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  <input
                    value={f.icon || ""}
                    onChange={(e) =>
                      setFeelings((p) =>
                        p.map((x) => (x.feeling_id === f.feeling_id ? { ...x, icon: e.target.value } : x))
                      )
                    }
                    onBlur={(e) => saveFeeling(f.feeling_id, { icon: e.target.value })}
                    className="w-10 rounded-md border border-amber-300/10 bg-[#080b16]/60 px-1 py-1 text-center text-sm outline-none focus:border-amber-300/40"
                  />
                  <input
                    data-testid={`feeling-label-${f.feeling_id}`}
                    value={f.label || ""}
                    onChange={(e) =>
                      setFeelings((p) =>
                        p.map((x) => (x.feeling_id === f.feeling_id ? { ...x, label: e.target.value } : x))
                      )
                    }
                    onBlur={(e) => saveFeeling(f.feeling_id, { label: e.target.value })}
                    className="flex-1 rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm text-amber-50 outline-none hover:border-amber-300/15 focus:border-amber-300/40"
                  />
                  <span className="rounded-full bg-amber-400/10 px-2 py-0.5 text-[11px] text-amber-300/80">
                    {f.experience_count}
                  </span>
                  <label className="flex items-center" title={f.active ? "Published" : "Hidden"}>
                    <input
                      data-testid={`feeling-active-${f.feeling_id}`}
                      type="checkbox"
                      checked={f.active !== false}
                      onChange={(e) => saveFeeling(f.feeling_id, { active: e.target.checked })}
                      className="h-4 w-4 accent-amber-400"
                    />
                  </label>
                  <div className="flex items-center">
                    <button onClick={() => reorderFeeling(i, -1)} disabled={i === 0} className="rounded p-1 text-amber-200 hover:bg-amber-400/10 disabled:opacity-25">
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => reorderFeeling(i, 1)} disabled={i === feelings.length - 1} className="rounded p-1 text-amber-200 hover:bg-amber-400/10 disabled:opacity-25">
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`feeling-delete-${f.feeling_id}`} onClick={() => removeFeeling(f)} className="rounded p-1 text-red-300/80 hover:bg-red-500/10">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {expandedId === f.feeling_id && (
                  <div className="border-t border-amber-300/10 bg-[#080b16]/30 p-3">
                    <div className="space-y-2">
                      {(expsByFeeling[f.feeling_id] || []).map((e, ei) => (
                        <div
                          key={e.experience_id}
                          data-testid={`story-row-${e.experience_id}`}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                            selectedExpId === e.experience_id
                              ? "border-amber-300/50 bg-amber-400/10"
                              : "border-amber-300/10 hover:border-amber-300/30"
                          }`}
                        >
                          <button
                            onClick={() => setSelectedExpId(e.experience_id)}
                            className="flex-1 text-left"
                          >
                            <span className="text-sm text-amber-50">{e.option_title}</span>
                            <span
                              className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                                e.active ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/10 text-amber-300/70"
                              }`}
                            >
                              {e.active ? "Published" : "Draft"}
                            </span>
                            <span className="ml-2 text-[10px] text-amber-100/40">{e.card_count} cards</span>
                          </button>
                          <button onClick={() => reorderStory(f.feeling_id, ei, -1)} disabled={ei === 0} className="rounded p-1 text-amber-200 hover:bg-amber-400/10 disabled:opacity-25">
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button onClick={() => reorderStory(f.feeling_id, ei, 1)} disabled={ei === (expsByFeeling[f.feeling_id] || []).length - 1} className="rounded p-1 text-amber-200 hover:bg-amber-400/10 disabled:opacity-25">
                            <ChevronDown className="h-3 w-3" />
                          </button>
                          <button onClick={() => duplicateStory(f.feeling_id, e.experience_id)} title="Duplicate" className="rounded p-1 text-amber-200 hover:bg-amber-400/10">
                            <Copy className="h-3 w-3" />
                          </button>
                          <button onClick={() => removeStory(f.feeling_id, e.experience_id)} className="rounded p-1 text-red-300/80 hover:bg-red-500/10">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      data-testid={`add-story-${f.feeling_id}`}
                      onClick={() => addStory(f.feeling_id)}
                      className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-amber-300/25 py-2 text-xs font-medium text-amber-200 hover:border-amber-300/50 hover:bg-amber-400/5"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add story
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* --- story editor --- */}
          <div className="rounded-2xl border border-amber-300/12 bg-[#0d1226]/40 p-5">
            {selectedExpId ? (
              <StoryEditor
                key={selectedExpId}
                eid={selectedExpId}
                previewUrl={
                  expandedId
                    ? `${window.location.origin}/bhagavatam/glimpse/${expandedId}`
                    : null
                }
                onSaved={() => expandedId && loadExps(expandedId)}
              />
            ) : (
              <div className="grid place-items-center py-24 text-center text-amber-100/40">
                <div>
                  <Sparkles className="mx-auto mb-3 h-8 w-8 text-amber-300/40" />
                  <p className="text-sm">Select a story to edit its cards, CTAs and settings.</p>
                  <p className="mt-1 text-xs">Expand a feeling on the left and pick a story.</p>
                </div>
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
};

// CSV export/import toolbar (English + a separate Hindi file). Image-preserving.
const CsvBtn = (p) => (
  <button
    {...p}
    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-1.5 text-xs font-medium text-amber-200 transition-all hover:bg-amber-400/10 disabled:opacity-50"
  />
);

const CsvToolbar = ({ onImported }) => {
  const enRef = useRef(null);
  const hiRef = useRef(null);
  const [busy, setBusy] = useState("");
  const doExport = async (lang) => {
    setBusy(`export-${lang}`);
    try {
      await exportGlimpseCsv(lang);
    } catch {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };
  const doImport = async (lang, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(`import-${lang}`);
    try {
      const res = await importGlimpseCsv(lang, file);
      toast.success(`Imported ${res.updated} rows (${lang.toUpperCase()})`);
      onImported && onImported();
    } catch {
      toast.error("Import failed — check the CSV columns");
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };
  const Btn = CsvBtn;
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-3">
      <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-amber-100/60">
        CSV
      </span>
      <Btn data-testid="export-en-btn" disabled={busy} onClick={() => doExport("en")}>
        <Download className="h-3.5 w-3.5" /> Export English
      </Btn>
      <Btn data-testid="export-hi-btn" disabled={busy} onClick={() => doExport("hi")}>
        <Download className="h-3.5 w-3.5" /> Export Hindi
      </Btn>
      <input ref={enRef} data-testid="import-en-input" type="file" accept=".csv" className="hidden" onChange={(e) => doImport("en", e)} />
      <input ref={hiRef} data-testid="import-hi-input" type="file" accept=".csv" className="hidden" onChange={(e) => doImport("hi", e)} />
      <Btn data-testid="import-en-btn" disabled={busy} onClick={() => enRef.current?.click()}>
        <Upload className="h-3.5 w-3.5" /> Import English
      </Btn>
      <Btn data-testid="import-hi-btn" disabled={busy} onClick={() => hiRef.current?.click()}>
        <Upload className="h-3.5 w-3.5" /> Import Hindi
      </Btn>
      <span className="ml-auto text-[11px] text-amber-100/35">
        Text-only import keeps uploaded card images
      </span>
    </div>
  );
};

// ---- Experience-level settings (entry text + navigation destinations) ----
const EXIT_OPTS = [
  { value: "feelings", label: "Feelings listing" },
  { value: "microsite_home", label: "Microsite home" },
  { value: "custom", label: "Custom URL" },
];
const HOME_OPTS = [
  { value: "microsite_home", label: "Microsite home" },
  { value: "custom", label: "Custom URL" },
];

const CTA_TYPES = [
  { value: "internal", label: "Internal route (/…)" },
  { value: "external", label: "External URL" },
  { value: "microsite_home", label: "Microsite home / section" },
  { value: "whatsapp", label: "WhatsApp" },
];

const ExperienceSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  useEffect(() => {
    adminGetGlimpseSettings()
      .then(setS)
      .catch(() => toast.error("Could not load settings"));
  }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setCta = (i, key, val) =>
    setS((p) => ({
      ...p,
      completion_ctas: (p.completion_ctas || []).map((c, ci) =>
        ci === i ? { ...c, [key]: val } : c
      ),
    }));
  const onEntryImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const { path } = await uploadGlimpseImage(file);
      set("entry_image", path);
      toast.success("Entry image uploaded (remember to Save)");
    } catch {
      toast.error("Upload failed (max 10MB · jpg/png/webp)");
    } finally {
      setImgBusy(false);
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminUpdateGlimpseSettings(s);
      setS(updated);
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };
  if (!s)
    return (
      <div className="grid place-items-center py-16 text-amber-100/40">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  return (
    <div className="max-w-2xl space-y-5 rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-6">
      <label className="flex items-center gap-2 text-sm text-amber-100/80">
        <input type="checkbox" checked={s.enabled !== false} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-amber-400" />
        Experience enabled (entry card shown on the Bhāgavatam path)
      </label>

      {/* Entry (promo) image shown on the Bhāgavatam Glimpse entry card */}
      <div className="rounded-xl border border-amber-300/10 bg-[#080b16]/40 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-amber-100/60">Entry image</p>
        <div className="flex items-center gap-3">
          <div className="grid h-20 w-32 shrink-0 place-items-center overflow-hidden rounded-lg border border-amber-300/10 bg-[#0d1226]">
            {s.entry_image ? (
              <img src={resolveMedia(s.entry_image)} alt={s.entry_image_alt || ""} className="h-full w-full object-cover" />
            ) : (
              <span className="text-xs text-amber-100/25">No image</span>
            )}
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-amber-300/25 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-400/10">
                {imgBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                {s.entry_image ? "Replace" : "Upload"}
                <input data-testid="settings-entry-image-input" type="file" accept="image/*" onChange={onEntryImage} className="hidden" />
              </label>
              {s.entry_image && (
                <button onClick={() => set("entry_image", "")} className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/20 px-3 py-1.5 text-xs text-red-300/80 hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
            <input value={s.entry_image_alt || ""} onChange={(e) => set("entry_image_alt", e.target.value)} placeholder="Alt text" className={`${inputCls} mt-2`} />
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Entry title</label>
          <input data-testid="settings-entry-title" value={s.entry_title || ""} onChange={(e) => set("entry_title", e.target.value)} className={inputCls} />
          <input value={s.entry_title_hi || ""} onChange={(e) => set("entry_title_hi", e.target.value)} placeholder="हिंदी शीर्षक" className={`${inputCls} mt-1.5`} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Entry meta (e.g. duration)</label>
          <input value={s.entry_meta || ""} onChange={(e) => set("entry_meta", e.target.value)} className={inputCls} />
          <input value={s.entry_meta_hi || ""} onChange={(e) => set("entry_meta_hi", e.target.value)} placeholder="हिंदी मेटा" className={`${inputCls} mt-1.5`} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Entry button label</label>
          <input value={s.entry_cta || ""} onChange={(e) => set("entry_cta", e.target.value)} className={inputCls} />
          <input value={s.entry_cta_hi || ""} onChange={(e) => set("entry_cta_hi", e.target.value)} placeholder="हिंदी बटन" className={`${inputCls} mt-1.5`} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Completion title</label>
          <input value={s.completion_title || ""} onChange={(e) => set("completion_title", e.target.value)} className={inputCls} />
          <input value={s.completion_title_hi || ""} onChange={(e) => set("completion_title_hi", e.target.value)} placeholder="हिंदी" className={`${inputCls} mt-1.5`} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Close (✕) destination</label>
          <select value={s.exit_destination || "feelings"} onChange={(e) => set("exit_destination", e.target.value)} className={inputCls}>
            {EXIT_OPTS.map((o) => (
              <option key={o.value} value={o.value} className="bg-[#0d1226]">{o.label}</option>
            ))}
          </select>
          {s.exit_destination === "custom" && (
            <input value={s.exit_custom_url || ""} onChange={(e) => set("exit_custom_url", e.target.value)} placeholder="/… or https://…" className={`${inputCls} mt-2`} />
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-amber-100/50">Home (⌂) destination</label>
          <select value={s.home_destination || "microsite_home"} onChange={(e) => set("home_destination", e.target.value)} className={inputCls}>
            {HOME_OPTS.map((o) => (
              <option key={o.value} value={o.value} className="bg-[#0d1226]">{o.label}</option>
            ))}
          </select>
          {s.home_destination === "custom" && (
            <input value={s.home_custom_url || ""} onChange={(e) => set("home_custom_url", e.target.value)} placeholder="/… or https://…" className={`${inputCls} mt-2`} />
          )}
        </div>
      </div>
      <div className="rounded-xl border border-amber-300/10 bg-[#080b16]/40 p-4">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-100/60">
          Completion CTAs (last card)
        </p>
        <p className="mb-3 text-[11px] text-amber-100/35">
          The action buttons on the final &ldquo;Only a Glimpse&rdquo; card of every
          story. Turn each on/off, rename it and set where it links.
        </p>
        <div className="space-y-3">
          {(s.completion_ctas || []).map((cta, i) => (
            <div
              key={cta.id || i}
              data-testid={`settings-cta-${cta.id || i}`}
              className={`rounded-xl border p-3 transition-colors ${
                cta.enabled === false
                  ? "border-amber-300/10 bg-[#0d1226]/40 opacity-70"
                  : "border-amber-300/20 bg-[#0d1226]/70"
              }`}
            >
              <div className="mb-2.5 flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs font-medium text-amber-100/80">
                  <input
                    data-testid={`settings-cta-enabled-${cta.id || i}`}
                    type="checkbox"
                    checked={cta.enabled !== false}
                    onChange={(e) => setCta(i, "enabled", e.target.checked)}
                    className="h-4 w-4 accent-amber-400"
                  />
                  Show
                </label>
                <span className="text-xs uppercase tracking-wider text-amber-300/60">
                  {cta.icon}
                </span>
                <label className="ml-auto flex items-center gap-1.5 text-xs text-amber-100/70">
                  <input
                    type="checkbox"
                    checked={!!cta.primary}
                    onChange={(e) => setCta(i, "primary", e.target.checked)}
                    className="h-3.5 w-3.5 accent-amber-400"
                  />
                  Primary (gold)
                </label>
              </div>
              <div className="grid gap-2.5 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] text-amber-100/45">Button label</label>
                  <input
                    data-testid={`settings-cta-label-${cta.id || i}`}
                    value={cta.label || ""}
                    onChange={(e) => setCta(i, "label", e.target.value)}
                    className={inputCls}
                  />
                  <input
                    value={cta.label_hi || ""}
                    onChange={(e) => setCta(i, "label_hi", e.target.value)}
                    placeholder="हिंदी लेबल"
                    className={`${inputCls} mt-1.5`}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-amber-100/45">Destination type</label>
                  <select
                    value={cta.type || "external"}
                    onChange={(e) => setCta(i, "type", e.target.value)}
                    className={inputCls}
                  >
                    {CTA_TYPES.map((o) => (
                      <option key={o.value} value={o.value} className="bg-[#0d1226]">
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    data-testid={`settings-cta-url-${cta.id || i}`}
                    value={cta.url || ""}
                    onChange={(e) => setCta(i, "url", e.target.value)}
                    placeholder="/bhagavatam/… or https://…"
                    className={`${inputCls} mt-1.5`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-amber-300/10 bg-[#080b16]/40 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-amber-100/60">
          Universal card font sizes (px)
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { key: "font_title", label: "Title", def: 24 },
            { key: "font_body", label: "Body text", def: 16 },
            { key: "font_eyebrow", label: "Tag / eyebrow", def: 11 },
            { key: "font_cta", label: "CTA text", def: 14 },
          ].map((f) => (
            <div key={f.key}>
              <label className="mb-1.5 block text-xs text-amber-100/50">{f.label}</label>
              <input
                data-testid={`settings-${f.key}`}
                type="number"
                min="8"
                max="48"
                value={s[f.key] ?? f.def}
                onChange={(e) => set(f.key, parseInt(e.target.value || f.def, 10))}
                className={inputCls}
              />
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-amber-100/35">
          Applies to every card. Bigger body text auto-shrinks the hero image so cards
          don&apos;t scroll.
        </p>
      </div>

      <button
        data-testid="settings-save-btn"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2 text-xs font-semibold text-[#0a0f24] hover:bg-amber-300 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        Save settings
      </button>
    </div>
  );
};
